// Minimal CMS shapes mirrored from the editor (apps/editor/src/store/editor.store.ts).
// The dashboard reads/writes these straight from project.data.projectContent.collections —
// it must NOT import from the editor app.
//
// La vigencia es la excepción: su cálculo vive en `@webbuilder/shared-types`,
// que las dos apps sí comparten. Copiarlo aquí daría dos definiciones de
// «cuándo se considera terminada una venta», y el día que discreparan el editor
// y el panel dirían cosas distintas del mismo dato.

import type { ScheduleWindow } from '@webbuilder/shared-types'
import { translate } from '../../lib/i18n'
import { firstImageUrl } from '../../lib/preview/projectContent'

export type CmsFieldType = 'text' | 'title' | 'richText' | 'code' | 'image' | 'multiImage' | 'date' | 'dateRange'

export type CmsField = { id: string; name: string; type: CmsFieldType }

export type CmsItem = { id: string; values: Record<string, string> }

export type CmsCollection = {
  id: string
  name: string
  createdAt?: string
  updatedAt?: string
  description?: string
  image?: string
  icon?: string
  color?: string
  hiddenFields?: string[]
  /**
   * Los campos de fecha que dan vigencia a las fichas.
   *
   * Se declara en el EDITOR, junto a los campos: es una decisión de estructura y
   * el panel no edita estructura. Aquí sólo se lee, para enseñar y filtrar por
   * estado — y para no perderla al guardar, que es lo que pasaría si la clave no
   * estuviera en el tipo y algún guardado reconstruyera la colección.
   */
  schedule?: ScheduleWindow
  fields: CmsField[]
  items: CmsItem[]
}

/** Title shown for a CMS item: its title-typed field, else common value keys, else the id. */
export function cmsItemTitle(collection: CmsCollection, item: CmsItem): string {
  const titleField = collection.fields.find((f) => f.type === 'title')
  const fromField = titleField ? item.values[titleField.id]?.trim() : ''
  return fromField || item.values.title?.trim() || item.values.name?.trim() || item.values.slug?.trim() || translate('cms.untitled')
}

/**
 * La imagen con la que se reconoce una ficha: la misma regla que la miniatura
 * de la tabla del editor.
 *
 * El valor de un campo de imagen es una LISTA guardada como JSON
 * (`["https://…"]`, ver `imageList` en el editor), no una dirección: puesta tal
 * cual en `src` salía la imagen rota. Se desempaqueta y vale la primera. Y se
 * recorren todos los campos de imagen —sencilla y varias— hasta dar con uno que
 * tenga algo: el primero suele ser «SEO · Imagen», casi siempre vacío.
 */
export function cmsItemImage(collection: CmsCollection, item: CmsItem): string | null {
  const fromFields = collection.fields
    .filter((f) => f.type === 'image' || f.type === 'multiImage')
    .map((f) => firstImageUrl(item.values[f.id]))
    .find(Boolean)
  const url = fromFields
    || firstImageUrl(item.values.image)
    || firstImageUrl(item.values.cover)
    || firstImageUrl(item.values.thumbnail)
    || firstImageUrl(item.values.ogImage)
  return url || null
}

export function slugifyCms(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'item'
}
